import { QueryClient } from '@tanstack/react-query';

/**
 * React Query client configuration
 * Optimized for performance with intelligent caching and retry strategies
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache configuration - Reduced for better refresh behavior
      staleTime: 0, // Always consider data stale for immediate refresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      
      // Network configuration
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      
      // Performance optimizations
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch when network reconnects
      refetchOnMount: true, // Refetch when component mounts
      
      // Background updates
      refetchInterval: false, // Disable automatic background refetching
      refetchIntervalInBackground: false,
      
      // Network mode
      networkMode: 'online', // Only run queries when online
    },
    mutations: {
      // Mutations are non-idempotent (POST/PATCH/DELETE). Retrying on failure
      // can cause duplicate side-effects (e.g. creating the same project twice
      // when the server returns 5xx or the response fails client-side parsing).
      retry: false,
      networkMode: 'online',
      
      // Global mutation callbacks for optimistic updates
      onMutate: async () => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        // This will be overridden by specific mutations as needed
      },
      onError: (error) => {
        // Global error handling for mutations
        console.error('Mutation failed:', error);
      },
      onSettled: () => {
        // Always refetch after error or success to ensure consistency
        // This will be overridden by specific mutations as needed
      },
    },
  },
});
